import {generateTokensFromObject} from '../__utils';
import {boxShadows as boxShadowsCore} from '../box-shadows';
import {coreColorTokens, coreColors} from '../colors';
import {controls} from '../controls';
import {opacities as sharedOpacities} from '../opacities';

import type {ThemeColors} from '../colors';
import type {Opacities} from '../opacities';
import type {ThemeObject} from '../theme.types';
import type {BoxShadows} from '../box-shadows';

/**
 * These values are inlined into <body> styles via jade to prevent flash of
 * unstyled content (FOUC) before the theme stylesheet loads.
 *
 * - `background` — body background. Keep in sync with the `background1` token
 *   in the colors object below.
 * - `loadingTrack` — preloader progress-bar track. Keep in sync with the
 *   `background2` token in the colors object below.
 * - `loadingBackground` — preloader screen background. White in light theme,
 *   black in dark themes. Not tied to a colors-object token.
 * - `loadingBloomOpacity` — opacity for the preloader's glow stack. Subtle on
 *   light backgrounds, full on dark backgrounds.
 * - `loadingText` — preloader text color. Mirrors the `textPrimary`
 *   token so loader copy stays readable on the loader background.
 */
export const DARK_DEFAULT_HTML_BACKGROUND_COLORS = {
  background: coreColors.d2.gray[1100],
  loadingTrack: coreColors.d2.gray[1000],
  loadingBackground: coreColors.d2.black,
  loadingBloomOpacity: 1,
  loadingText: coreColors.d2.white,
};

const colors: ThemeColors = {
  actionBlackGhostBackgroundHover: coreColorTokens.d2.blackA10,
  actionBlackGhostBackgroundSelected: coreColorTokens.d2.blackA19,
  actionPrimaryBackground: coreColorTokens.d2.blue[300],
  actionPrimaryBackgroundHover: coreColorTokens.d2.blue[200],
  actionPrimaryText: coreColorTokens.d2.white, // deprecated - using textPrimary
  actionPrimaryTextHover: coreColorTokens.d2.white, // deprecated - using textPrimary
  actionSecondaryBackground:
    'linear-gradient(180deg, rgba(255, 255, 255, 0.13) 0%, rgba(255, 255, 255, 0.11) 100%)',
  actionSecondaryBackgroundHover:
    'linear-gradient(180deg, rgba(255, 255, 255, 0.19) 0%, rgba(255, 255, 255, 0.17) 100%)',
  actionSecondaryBackgroundPressed:
    'linear-gradient(180deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.1) 100%)', // deprecated - using actionSecondaryBackground
  actionSecondarySelectedBackground: coreColorTokens.d2.blackA50,
  actionSecondarySelectedBackgroundPressed: coreColorTokens.d2.blackA50, // deprecated - using actionSecondarySelectedBackground
  actionSecondaryText: coreColorTokens.d2.white, // deprecated - using textPrimary
  actionSecondaryTextHover: coreColorTokens.d2.white, // deprecated - using textPrimary
  actionSecondaryBorder: coreColorTokens.d2.whiteA22,
  actionSecondaryBorderHover: coreColorTokens.d2.whiteA30,
  actionWhiteGhostBackgroundHover: coreColorTokens.d2.whiteA7,
  actionWhiteGhostBackgroundSelected: coreColorTokens.d2.whiteA10,
  actionWhiteOutlineBackgroundSelected: coreColorTokens.d2.whiteA10,
  actionWhiteOutlineBorder: coreColorTokens.d2.whiteA13,
  actionWhiteOutlineBorderHover: coreColorTokens.d2.whiteA22,
  actionWhiteSolidBackgroundHover: coreColorTokens.d2.gray[300],
  /**
   * Simplifying background colors.
   * The previous background 1 ... 5 need to be consolidated (renaming callsites)
   * to the primary ... tertiary values.
   *
   * @see https://webflow.atlassian.net/browse/DS-761
   */
  backgroundPrimary: coreColorTokens.d2.gray[1100],
  backgroundPrimaryDisabled: coreColorTokens.d2.gray[900],
  backgroundSecondary: coreColorTokens.d2.gray[1000],
  backgroundTertiary: coreColorTokens.d2.gray[800],
  backgroundRaised: coreColorTokens.d2.whiteA7,
  backgroundHighlight: '#080808',
  background1: coreColorTokens.d2.gray[1100],
  background2: coreColorTokens.d2.gray[1000],
  background3: coreColorTokens.d2.gray[800],
  background4: coreColorTokens.d2.gray[900],
  background5: coreColorTokens.d2.gray[800],
  backgroundInactive: coreColorTokens.d2.gray[1100],
  backgroundInverse: coreColorTokens.d2.gray[100],
  backgroundInverseHover: coreColorTokens.d2.gray[100], // deprecated - using backgroundInverse
  blueBackground: coreColorTokens.d2.blue[300],
  blueBackgroundDisabled: coreColorTokens.d2.blue[700],
  blueBackgroundHover: coreColorTokens.d2.blue[200],
  blueBackgroundPressed: coreColorTokens.d2.blue[300],
  blueBorder: coreColorTokens.d2.blue[400],
  blueCanvas: coreColorTokens.d2.blue[400],
  blueCanvasTransparent: coreColorTokens.d2.blue.A10,
  blueChart: coreColorTokens.d2.blue[400],
  blueChart2: coreColorTokens.d2.blue[100],
  blueIcon: coreColorTokens.d2.blue[100],
  blueOutline: `0 0 0 2px ${coreColorTokens.blue.A20}`,
  blueText: coreColorTokens.d2.blue[100],
  blueTransparent: coreColorTokens.d2.blue.A15,
  blueTransparentHover: coreColorTokens.d2.blue.A25,
  /**
   * Simplifying border colors.
   * The previous border 1 ... 4 need to be consolidated (renaming callsites)
   * to the new border values.
   *
   * @see https://webflow.atlassian.net/browse/DS-740
   */
  borderDefault: coreColorTokens.d2.whiteA13, // $border1
  borderTextInput: coreColorTokens.d2.whiteA19, // $border2
  borderCheckboxRadio: coreColorTokens.d2.whiteA22, // $border3
  border1: coreColorTokens.d2.whiteA13,
  border2: coreColorTokens.d2.whiteA19,
  border3: coreColorTokens.d2.whiteA22,
  border4: coreColorTokens.d2.whiteA22, // deprecated - using border3
  brandIconDetail: coreColorTokens.d2.gray[525],
  brandIconFocus: coreColorTokens.d2.white,
  brandIconOutline: coreColorTokens.d2.brandBlue[200],
  chartSeriesTeal: '#71FFFF',
  chartSeriesOrange: '#FF9442',
  chartSeriesLightPink: '#EE94ED',
  chartSeriesGreen: '#409A30',
  chartSeriesPink: '#FF3199',
  chartSeriesYellow: '#FFF08E',
  chartSeriesBlue: '#0082FF',
  chartSeriesGray: '#B8B8B8',
  closeButtonBackground: coreColorTokens.d2.whiteA10,
  grayIcon: coreColorTokens.d2.gray[400],
  grayTransparent: coreColorTokens.d2.whiteA19,
  grayBackground: coreColorTokens.d2.gray[700],
  grayBackgroundHover: coreColorTokens.d2.gray[700],
  grayBackgroundPressed: coreColorTokens.d2.gray[700],
  grayBorder: coreColorTokens.d2.gray[750],
  grayChart: coreColorTokens.d2.gray[500],
  grayCanvas: coreColorTokens.d2.gray[600],
  grayCanvasHover: coreColorTokens.d2.gray[700],
  grayCanvasTransparent: coreColorTokens.d2.whiteA10,
  grayBackgroundTransparent: coreColorTokens.d2.whiteA19,
  grayBackgroundTransparentHover: coreColorTokens.d2.whiteA22,
  grayBackgroundDisabled: coreColorTokens.d2.gray[900],
  greenBackground: coreColorTokens.d2.green[300],
  greenBackgroundDisabled: coreColorTokens.d2.green[700],
  greenBackgroundHover: coreColorTokens.d2.green[200],
  greenBackgroundPressed: coreColorTokens.d2.green[300],
  greenBorder: coreColorTokens.d2.green[400],
  greenCanvas: coreColorTokens.d2.green[400],
  greenCanvasTransparent: coreColorTokens.d2.green.A10,
  greenChart: coreColorTokens.d2.green[100],
  greenIcon: coreColorTokens.d2.green[100],
  greenOutline: `0 0 0 2px ${coreColorTokens.green.A20}`,
  greenText: coreColorTokens.d2.green[100],
  greenTransparent: coreColorTokens.d2.green.A25,
  greenTransparentHover: coreColorTokens.d2.green.A35,
  orangeBackground: coreColorTokens.d2.orange[300],
  orangeBackgroundDisabled: coreColorTokens.d2.orange[700],
  orangeBackgroundHover: coreColorTokens.d2.orange[200],
  orangeBackgroundPressed: coreColorTokens.d2.orange[300],
  orangeBorder: coreColorTokens.d2.orange[400],
  orangeCanvas: coreColorTokens.d2.orange[400],
  orangeCanvasTransparent: coreColorTokens.d2.orange.A10,
  orangeChart: coreColorTokens.d2.orange[400],
  orangeIcon: coreColorTokens.d2.orange[100],
  orangeText: coreColorTokens.d2.orange[100],
  orangeTransparent: coreColorTokens.d2.orange.A20,
  orangeTransparentHover: coreColorTokens.d2.orange.A30,
  pinkBackground: coreColorTokens.d2.pink[300],
  pinkBackgroundDisabled: coreColorTokens.d2.pink[700],
  pinkBackgroundHover: coreColorTokens.d2.pink[200],
  pinkBackgroundPressed: coreColorTokens.d2.pink[300],
  pinkBorder: coreColorTokens.d2.pink[400],
  pinkCanvas: coreColorTokens.d2.pink[400],
  pinkCanvasTransparent: coreColorTokens.d2.pink.A10,
  pinkChart: coreColorTokens.d2.pink[400],
  pinkIcon: coreColorTokens.d2.pink[100],
  pinkOutline: `0 0 0 2px ${coreColorTokens.pink.A20}`,
  pinkText: coreColorTokens.d2.pink[100],
  pinkTransparent: coreColorTokens.d2.pink.A25,
  pinkTransparentHover: coreColorTokens.d2.pink.A35,
  // These are currently identical across all color themes and hard-coded in many places.
  // This may change as themes and presence tokens evolve.
  presenceDisabledBlue: '#316972',
  presenceDisabledGreen: '#516A39',
  presenceDisabledOrange: '#754C2E',
  presenceDisabledPink: '#6E365E',
  presenceDisabledRed: '#6E3638',
  presenceDisabledYellow: '#75692E',
  presenceCanvasBlue: '#0096B180',
  presenceCanvasGreen: '#5A9A1B80',
  presenceCanvasOrange: '#D45E0B80',
  presenceCanvasPink: '#DE2CAD80',
  presenceCanvasRed: '#EF313D80',
  presenceCanvasYellow: '#A0830080',
  presenceOutlineBlue: '#8DD9E7',
  presenceOutlineGreen: '#B3F076',
  presenceOutlineOrange: coreColorTokens.d2.orange[100],
  presenceOutlinePink: coreColorTokens.d2.pink[100],
  presenceOutlineRed: coreColorTokens.d2.red[100],
  presenceOutlineYellow: '#FFE372',
  presencePrimaryBlue: '#8DD9E7',
  presencePrimaryGreen: '#B3F076',
  presencePrimaryOrange: coreColorTokens.d2.orange[100],
  presencePrimaryPink: coreColorTokens.d2.pink[100],
  presencePrimaryRed: coreColorTokens.d2.red[100],
  presencePrimaryYellow: '#FFE372',
  presenceSecondaryBlue: '#00B8D933',
  presenceSecondaryGreen: '#80D72833',

  presenceSecondaryOrange: coreColorTokens.d2.orange.A25,
  presenceSecondaryPink: coreColorTokens.d2.pink.A25,
  presenceSecondaryRed: coreColorTokens.d2.red.A25,
  presenceSecondaryYellow: coreColorTokens.d2.yellow.A20,
  purpleBackground: coreColorTokens.d2.purple[300],
  purpleBackgroundDisabled: coreColorTokens.d2.purple[700],
  purpleBackgroundHover: coreColorTokens.d2.purple[200],
  purpleBackgroundPressed: coreColorTokens.d2.purple[300],
  purpleBorder: coreColorTokens.d2.purple[400],
  purpleCanvas: coreColorTokens.d2.purple[400],
  purpleCanvasTransparent: coreColorTokens.d2.purple.A10,
  purpleChart: coreColorTokens.d2.purple[400],
  purpleChart2: coreColorTokens.d2.purple[100],
  purpleIcon: coreColorTokens.d2.purple[100],
  purpleOutline: `0 0 0 2px ${coreColorTokens.purple.A20}`,
  purpleText: coreColorTokens.d2.purple[100],
  purpleTransparent: coreColorTokens.d2.purple.A25,
  purpleTransparentHover: coreColorTokens.d2.purple.A35,
  redBackground: coreColorTokens.d2.red[400],
  redBackgroundDisabled: coreColorTokens.d2.red[600],
  redBackgroundHover: coreColorTokens.d2.red[300],
  redBackgroundPressed: coreColorTokens.d2.red[300],
  redBorder: coreColorTokens.d2.red[400],
  redCanvas: coreColorTokens.d2.red[400],
  redCanvasTransparent: coreColorTokens.d2.red.A10,
  redChart: coreColorTokens.d2.red[100],
  redIcon: coreColorTokens.d2.red[100],
  redOutline: `0 0 0 2px ${coreColorTokens.red.A20}`,
  redText: coreColorTokens.d2.red[100],
  redTransparent: coreColorTokens.d2.red.A25,
  redTransparentHover: coreColorTokens.d2.red.A35,
  skeleton: coreColorTokens.d2.whiteA7,
  textBlackPrimary: coreColorTokens.d2.black,
  textBlackSecondary: coreColorTokens.d2.blackA60,
  textDimmed: coreColorTokens.d2.whiteA50,
  textInverse: coreColorTokens.d2.black,
  textPrimary: coreColorTokens.d2.white,
  textSecondary: coreColorTokens.d2.whiteA67,
  textWhitePrimary: coreColorTokens.d2.white,
  textWhiteSecondary: coreColorTokens.d2.whiteA67,
  uiAutocompleteListBoxBackground: coreColorTokens.d2.gray[800], // $backgroundTertiary
  uiAvatarColoredTextDisabled: coreColorTokens.d2.whiteA40,
  uiAvatarDefaultTextDisabled: coreColorTokens.d2.whiteA40,
  uiAvatarShadow: coreColorTokens.d2.whiteA10,
  uiBackgroundActive: coreColorTokens.d2.whiteA10,
  uiBackgroundHover: coreColorTokens.d2.whiteA7,
  uiBadgeBackground: coreColorTokens.d2.gray[100],
  uiBadgeBackgroundHover: coreColorTokens.d2.gray[300],
  uiBadgeBackgroundPressed: coreColorTokens.d2.gray[100],
  uiBlackGhostBorder: coreColorTokens.d2.blackA19,
  uiBlackGhostBorderHover: coreColorTokens.d2.blackA30,
  uiBorderSolid: '#464646',
  uiCardBackground: coreColorTokens.d2.whiteA7,
  uiCanvasBackground: coreColorTokens.d2.gray[800],
  uiCheckboxRadioBorderColor: coreColorTokens.d2.whiteA22, // $border3
  uiDragOverlayBackground: coreColorTokens.d2.gray[800],
  uiInputBackground: coreColorTokens.d2.blackA22,
  uiInputBorderColor: coreColorTokens.d2.whiteA19, // $border2
  uiInputBorderColorHover: coreColorTokens.d2.whiteA22, // $border3
  uiFuseControlFilter:
    'drop-shadow(0px -0.5px 0px rgba(255, 255, 255, 0.20)) drop-shadow(0px 0px 1px rgba(0, 0, 0, 0.4)) drop-shadow(0px 1px 0px rgba(0, 0, 0, 0.2))',
  uiFuseControlHover: 'brightness(1.28)',
  uiFuseControlPressed: 'brightness(0.8)',
  uiMenuContentBackground: coreColorTokens.d2.gray[800],
  uiMenuContentBackgroundLight: coreColorTokens.d2.gray[1000], // $background2
  uiMenuDivider: coreColorTokens.d2.whiteA13, // $border1
  uiModalBackdrop: coreColorTokens.d2.blackA60,
  uiNumberInputButtonIconHoverColor:
    'linear-gradient(180deg, rgba(255, 255, 255, 0.19) 0%, rgba(255, 255, 255, 0.17) 100%)',
  uiNumberInputStepperBorder: 'none',
  uiNumberInputStepperInputBorder: coreColorTokens.d2.whiteA19, // $border2
  uiNumberInputValueWrapperOutline: 'none',
  uiNumberInputValueWrapperOutlineHover: 'none',
  uiPopoverBackdrop: coreColorTokens.d2.blackA50,
  uiSegmentedControlActive: coreColorTokens.d2.blackA50,
  uiSelectBackground:
    'linear-gradient(180deg, rgba(255, 255, 255, 0.13) 0%, rgba(255, 255, 255, 0.11) 100%)',
  uiSelectBackgroundHover:
    'linear-gradient(180deg, rgba(255, 255, 255, 0.19) 0%, rgba(255, 255, 255, 0.17) 100%)',
  // We're rendering intentionally invalid CSS here in dark themes to get the result we need (border in light theme, no border in dark theme)
  // See GitHub comment thread: https://github.com/webflow/webflow/pull/68627#discussion_r1600646053
  uiSelectBorder: 'none',
  uiScrollbarThumb: coreColorTokens.d2.whiteA30,
  uiScrollbarThumbHover: coreColorTokens.d2.whiteA40,
  uiTableRowActiveBackground: 'rgba(255, 255, 255, 0.03)',
  uiTableZebraStripeBackground: 'rgba(255, 255, 255, 0.02)',
  yellowBackground: coreColorTokens.d2.yellow[400],
  yellowBackgroundDisabled: coreColorTokens.d2.yellow[700],
  yellowBackgroundHover: coreColorTokens.d2.yellow[100],
  yellowBackgroundPressed: coreColorTokens.d2.yellow[400],
  yellowBorder: coreColorTokens.d2.yellow[400],
  yellowCanvas: coreColorTokens.d2.yellow[400],
  yellowCanvasTransparent: coreColorTokens.d2.yellow.A10,
  yellowChart: coreColorTokens.d2.yellow[400],
  yellowIcon: coreColorTokens.d2.yellow[400],
  yellowOutline: `0 0 0 2px ${coreColorTokens.yellow.A20}`,
  yellowText: coreColorTokens.d2.yellow[400],
  yellowTransparent: coreColorTokens.d2.yellow.A15,
  yellowTransparentHover: coreColorTokens.d2.yellow.A25,
};

const boxShadows: BoxShadows = {
  ...boxShadowsCore,
  actionColored:
    '0px 0.5px 1px 0px rgba(0, 0, 0, 0.8), 0px 0.5px 0.5px 0px rgba(255, 255, 255, 0.20) inset',
  actionSecondary:
    '0px 0.5px 1px rgba(0, 0, 0, 0.8), inset 0px 0.5px 0.5px rgba(255, 255, 255, 0.12)',
  inputInner:
    '0px 1px 1px -1px rgba(0, 0, 0, 0.13) inset, 0px 3px 3px -3px rgba(0, 0, 0, 0.17) inset, 0px 4px 4px -4px rgba(0, 0, 0, 0.17) inset, 0px 8px 8px -8px rgba(0, 0, 0, 0.17) inset, 0px 12px 12px -12px rgba(0, 0, 0, 0.13) inset, 0px 16px 16px -16px rgba(0, 0, 0, 0.13) inset',
  menu: '0px 12px 24px 8px rgba(0, 0, 0, 0.08), 0px 8px 16px 4px rgba(0, 0, 0, 0.08), 0px 4px 8px 2px rgba(0, 0, 0, 0.08), 0px 2px 6px 0px rgba(0, 0, 0, 0.08), 0px -0.5px 0.5px 0px rgba(0, 0, 0, 0.12) inset, 0px 0.5px 0.5px 0px rgba(255, 255, 255, 0.12) inset',
  searchableList: '0px -1px 0px 0px rgba(255, 255, 255, .13)',
  segmentedControl:
    'inset 0px 16px 16px -16px rgba(0, 0, 0, 0.2), inset 0px 12px 12px -12px rgba(0, 0, 0, 0.2), inset 0px 8px 8px -8px rgba(0, 0, 0, 0.25), inset 0px 4px 4px -4px rgba(0, 0, 0, 0.25), inset 0px 3px 3px -3px rgba(0, 0, 0, 0.25), inset 0px 1px 1px -1px rgba(0, 0, 0, 0.3) !important',
  uiNumberInputDefaultButtonActive:
    'inset 0px 0.5px 0.5px rgba(255, 255, 255, 0.12)',
  uiNumberInputDefaultButtonInactive:
    '0px 0.5px 1px rgba(0, 0, 0, 0.8), inset 0px 0.5px 0.5px rgba(255, 255, 255, 0.12)',
  uiNumberInputStepper:
    '0px 0.5px 1px 0px rgba(0, 0, 0, 0.80), 0px 0.5px 0.5px 0px rgba(255, 255, 255, 0.12) inset',
  uiSelect:
    '0px 0.5px 1px rgba(0, 0, 0, 0.8), inset 0px 0.5px 0.5px rgba(255, 255, 255, 0.12)',
};

const opacities: Opacities = {
  ...sharedOpacities,
  uiFuseControlTop: 0.12,
  uiFuseControlInnerSide: 0.094,
  uiFuseControlOuterSide: 0.094,
  uiFuseControlInnerBottom: 0.064,
  uiFuseControlOuterBottom: 0.064,
};

export const darkDefaultTheme: ThemeObject = {
  boxShadows,
  colors,
  controls,
  opacities,
};

export const darkDefaultCSSVariables =
  generateTokensFromObject(darkDefaultTheme).setTokens;
